import type { ChatTimelineItem } from "./buildTimeline";

/** Snapshot of the scroll container taken after the previous render. */
export interface ScrollAnchorSnapshot {
  scrollHeight: number;
  firstMessageId: string | null;
  lastMessageId: string | null;
  lastSender: string | null;
}

export type ScrollAdjustment =
  | { kind: "pin-bottom" }
  | { kind: "preserve"; delta: number }
  | { kind: "none" };

export function messageBounds(items: ChatTimelineItem[]): {
  firstMessageId: string | null;
  lastMessageId: string | null;
  lastSender: string | null;
} {
  let first: string | null = null;
  let last: string | null = null;
  let lastSender: string | null = null;
  for (const item of items) {
    if (item.type !== "message") continue;
    if (first === null) first = item.message.id;
    last = item.message.id;
    lastSender = item.message.sender;
  }
  return { firstMessageId: first, lastMessageId: last, lastSender };
}

/**
 * Decide how the scroll position should move after the timeline changed.
 *
 * - First render (no snapshot) or an empty previous thread: pin to bottom.
 * - Older rows were prepended (first id changed, last id unchanged): keep the
 *   viewer's place by shifting `scrollTop` by the height that was added.
 * - A new message arrived at the end: pin to bottom when the viewer was
 *   already at the bottom or when the newest message is the operator's own.
 * - Otherwise (status ticks, reactions): stay pinned only if already at bottom.
 */
export function resolveScrollAdjustment({
  previous,
  next,
  nextScrollHeight,
  atBottom,
}: {
  previous: ScrollAnchorSnapshot | null;
  next: Omit<ScrollAnchorSnapshot, "scrollHeight">;
  nextScrollHeight: number;
  atBottom: boolean;
}): ScrollAdjustment {
  if (!previous || previous.lastMessageId === null) {
    return { kind: "pin-bottom" };
  }
  const prepended =
    next.firstMessageId !== null &&
    previous.firstMessageId !== next.firstMessageId &&
    previous.lastMessageId === next.lastMessageId;
  if (prepended) {
    return {
      kind: "preserve",
      delta: nextScrollHeight - previous.scrollHeight,
    };
  }
  const appended = previous.lastMessageId !== next.lastMessageId;
  if (appended && (atBottom || next.lastSender === "operator")) {
    return { kind: "pin-bottom" };
  }
  if (atBottom) return { kind: "pin-bottom" };
  return { kind: "none" };
}

/** True when the container is scrolled to (or within `threshold` px of) the end. */
export function isScrolledToBottom(
  node: { scrollHeight: number; scrollTop: number; clientHeight: number },
  threshold = 48,
): boolean {
  return node.scrollHeight - node.scrollTop - node.clientHeight <= threshold;
}
